import React, { ReactNode, useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';

import DataFormat from '../../format/DataFormat';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';
import resolveKey from '../../../utils/components/_resolveKey';
import resolveStringValue from '../../../utils/components/_resolveStringValue';

import CheckboxItem, { CheckboxItemProps } from '../CheckboxItem';
import { StyledCheckboxListItemWrapper, StyledCheckboxList, StyledTextOnlyWrapper } from './CheckboxList.styles';

export interface CheckboxListOption<Value> {
  /** Label of the option. */
  label?: ReactNode;
  /** When "value" is an object, which property to use as the unique key */
  key?: string | string[];
  /** Defines the current value of the `CheckboxList`. Must be an array. */
  value: Value;
  /** Disables a single option. */
  disabled?: boolean;
}

export interface CheckboxListProps<Value> extends Omit<CheckboxItemProps, 'value'> {
  /** List of options in the list */
  options?: CheckboxListOption<Value>[];
  /** Display the list horizontally. */
  horizontal?: boolean;
  value?: Value[];
  noBox?: boolean;
  emptyDataSeparator?: ReactNode;
  /** @ignore */
  isValid?: boolean;
  /** @ignore */
  hasWarning?: boolean;
  /** @ignore */
  hasCaution?: boolean;
  /** @ignore */
  hasInfo?: boolean;
  /** @ignore */
  isTouched?: boolean;
  /** @ignore */
  disabled?: boolean;
  /** @ignore */
  readOnly?: boolean;
  /** @ignore */
  textOnly?: boolean;
}

const CheckboxListDefaultProps: Partial<CheckboxListProps<any>> = {
  options: [],
  value: [],
  emptyDataSeparator: <br />,
};

/**
 * @mode developer
 */
const CheckboxList = <Value extends null | object | string | number | boolean, >({
  id, options, value, horizontal, noBox, name, isValid, hasWarning, hasCaution, hasInfo, isTouched, disabled, readOnly, textOnly, emptyDataSeparator, ...props
}: CheckboxListProps<Value>) => {
  const { scheme } = useContext(ColorSchemeContext);
  const theme = useContext(ThemeContext);
  const resolvedHorizontal = (typeof horizontal !== 'undefined') ? horizontal : theme?.CheckboxList?.horizontal;

  const resolvedNoBox = useMemo(() => {
    if (theme?.CheckboxList?.forceBoxOnStatus && isTouched && (hasWarning || hasCaution || hasInfo || !isValid)) return false;
    return (typeof noBox !== 'undefined') ? noBox : theme?.CheckboxList?.noBox;
  }, [isTouched, hasCaution, hasInfo, hasWarning, isValid, noBox, theme?.CheckboxList?.forceBoxOnStatus, theme?.CheckboxList?.noBox]);

  if (textOnly) {
    const selectedOptions = Array.isArray(value) ?
      options.filter((option) => value.includes(option.value)) :
      '';

    if (typeof selectedOptions === 'string' || !selectedOptions.length) {
      return <DataFormat />;
    }

    return (
      <StyledTextOnlyWrapper>
        {selectedOptions.map(({ value: optionValue, key, label }, index) => (
          <React.Fragment key={resolveKey(optionValue, key)}>
            <DataFormat>{label}</DataFormat>
            {(index < selectedOptions.length - 1) && emptyDataSeparator}
          </React.Fragment>
        ))}
      </StyledTextOnlyWrapper>
    );
  }

  return (
    <StyledCheckboxList
      id={id}
      data-control
      shaded={scheme === COLOR_SCHEMES.SHADE}
      data-testid="CheckboxList"
      data-test-name={name}
      horizontal={resolvedHorizontal}
      noBox={resolvedNoBox}
      isValid={isValid}
      hasWarning={hasWarning}
      hasCaution={hasCaution}
      hasInfo={hasInfo}
      isTouched={isTouched}
      disabled={disabled}
      readOnly={readOnly}
    >
      <StyledCheckboxListItemWrapper
        horizontal={resolvedHorizontal}
        noBox={resolvedNoBox}
        isValid={isValid}
        hasWarning={hasWarning}
        hasCaution={hasCaution}
        hasInfo={hasInfo}
        isTouched={isTouched}
      >
        {options.map(({ label, value: optionValue, key, disabled: optionDisabled }, index) => (
          <CheckboxItem
            {...props}
            name={name}
            data-index={index}
            key={resolveKey(optionValue, key)}
            label={label}
            value={resolveStringValue(optionValue, key)}
            checked={value.some((selectedOption) => resolveStringValue(selectedOption, key) === resolveStringValue(optionValue, key))}
            horizontal={resolvedHorizontal}
            noBox={resolvedNoBox}
            disabled={typeof optionDisabled !== 'undefined' ? optionDisabled : disabled}
            readOnly={readOnly}
          />
        ))}
      </StyledCheckboxListItemWrapper>
    </StyledCheckboxList>
  );
};

CheckboxList.defaultProps = CheckboxListDefaultProps;

export default CheckboxList;
